<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="recursos"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>

    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de recursos</custom-title>
      </template>

      <template #right>
        <template v-if="!editingCosts">
          <el-tooltip
            v-if="!canEdit"
            content="El usuario no posee permisos de edición"
            placement="top"
          >
            <el-button
              type="primary"
              size="default"
              :icon="IconAdd"
              disabled
            >
              Nuevo recurso
            </el-button>
          </el-tooltip>

          <template v-else>
            <el-button
              type="primary"
              size="default"
              :icon="IconAdd"
              @click="openModal"
            >
              Nuevo recurso
            </el-button>
            <modal-new-resource
              v-model="modalVisible"
              @closed="handleModalClosed"
            />

            <el-button
              type="warning"
              size="default"
              :icon="IconCashOpen"
              @click="enterCostMode"
            >
              Actualizar Costos
            </el-button>
          </template>
        </template>

        <template v-else>
          <el-button
            type="success"
            size="default"
            :icon="IconContentSave"
            @click="saveCosts"
          >
            Guardar Costos
          </el-button>
          <el-button
            type="info"
            size="default"
            :icon="IconContentSaveMinus"
            @click="cancelCosts"
          >
            Cancelar
          </el-button>
        </template>
      </template>
    </main-buttons-bar>

    <div v-if="!editingCosts">
      <resource-filters></resource-filters>
    </div>
    <div
      v-else
      class="cost-panel"
    >
      <custom-input-number
        v-model="adjustmentPercent"
        :spanColumn="3"
        label="Porcentaje de ajuste"
        :settings="{
          placeholder: 'Porcentaje',
          min: -100,
          precision: 2,
          step: 1,
          controls: false,
        }"
      />
      <el-button
        size="default"
        type="primary"
        @click="applyAdjustment"
      >
        Aplicar porcentaje
      </el-button>
    </div>

    <custom-table
      :requestApi="findAllRecursoHTTP"
      :pathDetail="'/page/resource/detail'"
      :pathEdit="'/page/resource/edit'"
      :paramId="'Codigo'"
      :data="recursoList"
      :columns="columns"
      :showDetailButton="false"
    >
      <template #CostoHora="{ row }">
        <template v-if="editingCosts">
          <custom-input-number
            v-model="row._editCosto"
            :settings="{
              placeholder: 'Costo hora',
              min: 0,
              precision: 2,
              step: 0.01,
              controls: false,
            }"
          />
        </template>
        <template v-else>
          {{ row.CostoHora != null ? row.CostoHora : '-' }}
        </template>
      </template>
      <template #custom-actions="{ row }">
        <el-tooltip
          v-if="!canEdit"
          content="El usuario no posee permisos de edición"
          placement="top"
        >
          <el-button
            :icon="IconContentCopy"
            disabled
          />
        </el-tooltip>
        <el-button
          v-else
          :icon="IconContentCopy"
          @click.prevent="duplicateRow(row)"
        />
      </template>
    </custom-table>
  </permission-gate>
</template>
<script setup>
import {
  ref,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRecursoStore } from '@/stores/recurso_store.js';
import { useRecursoTipoStore } from '@/stores/recurso_tipo_store.js';
import { useLoading } from '@/shared/composables/loading.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconAdd from '~icons/mdi/add';
import IconContentCopy from '~icons/mdi/content-copy';
import ModalNewResource from './modal_new_resource.vue';
import ResourceFilters from './resource_filters.vue';
import PermissionGate from '@/shared/components/permission_gate.vue';
import IconCashOpen from '~icons/mdi/cash-lock-open';
import IconContentSave from '~icons/mdi/content-save-check';
import IconContentSaveMinus from '~icons/mdi/content-save-minus-outline';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';

const recursoStore = useRecursoStore();
const recursoTipoStore = useRecursoTipoStore();
const { firstRecurso, findAllRecursoHTTP, resetResource } = recursoStore;
const { recursoList } = storeToRefs(recursoStore);
const { firstRecursoTipo } = recursoTipoStore;
// local state
const items = [
  { label: 'Inicio', to: '/dashboard', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const editingCosts = ref(false);
const adjustmentPercent = ref(0);
const originalCosts = ref([]);

const columns = [
  { field: 'Codigo', label: 'Código', width: 80, sortable: true, isVisible: true },
  { field: 'Descripcion', label: 'Descripción', width: 200, sortable: true, isVisible: true },
  {
    field: 'RecursoTipo.Descripcion',
    label: 'Tipo Recurso',
    width: 220,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CodigoRenglonEquivalente',
    label: 'Código Equivalente',
    width: 150,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CostoHora',
    label: 'Costo hora',
    width: 140,
    sortable: true,
    isVisible: true,
    slot: 'CostoHora',
  },
  { field: 'Moneda', label: 'Moneda', width: 80, sortable: true, isVisible: true },
  { field: 'MetrosColor', label: 'Metros color', width: 120, sortable: true, isVisible: true },
  { field: 'Activo', label: 'Activo', width: 100, sortable: true, isVisible: true },
];
const modalVisible = ref(false);

const openModal = () => {
  modalVisible.value = true;
};

function enterCostMode() {
  originalCosts.value = recursoList.value.map((r) => ({
    Codigo: r.Codigo,
    CostoHora: r.CostoHora || 0,
  }));

  recursoList.value.forEach((r) => {
    r._editCosto = r.CostoHora || 0;
  });

  editingCosts.value = true;
}

function applyAdjustment() {
  const pct = adjustmentPercent.value / 100;
  recursoList.value.forEach((r) => {
    const orig = originalCosts.value.find((o) => o.Codigo === r.Codigo);
    const baseCost = orig ? orig.CostoHora : 0;

    r._editCosto = +(baseCost * (1 + pct)).toFixed(2);
  });
}

function cancelCosts() {
  recursoList.value.forEach((r) => {
    delete r._editCosto;
  });
  editingCosts.value = false;
  adjustmentPercent.value = 0;
  originalCosts.value = [];
}

async function saveCosts() {
  const toUpdate = recursoList.value
    .filter((r) => r._editCosto !== (r.CostoHora || 0))
    .map((r) => ({ Codigo: r.Codigo, CostoHora: r._editCosto }));

  if (toUpdate.length === 0) {
    ElMessage.info('No hay cambios en los costos para guardar.');
    cancelCosts();
    return;
  }

  try {
    await ElMessageBox.confirm(
      `Se actualizarán los costos de ${toUpdate.length} Recursos. ¿Confirma la actualización?`,
      'Confirmar actualización',
      { type: 'warning' },
    );

    useLoading().openLoading();
    await recursoStore.updateRecursoCostosHTTP(toUpdate);

    useLoading().closeLoading();
    ElMessage.success('Costos guardados correctamente');

    cancelCosts();
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('Error al guardar los costos.');
      console.error(error);
    }
    useLoading().closeLoading();
  }
}

const handleModalClosed = () => {
  modalVisible.value = false;
};

async function duplicateRow(row) {
  const loading = useLoading();
  try {
    loading.openLoading();
    const response = await recursoStore.cloneResourceHTTP(row.Codigo);

    if (response && response.success) {
      ElMessage.success('Recurso duplicado correctamente');
      await recursoStore.firstRecurso();
    }
  } catch (error) {
    ElMessage.error('Ocurrió un error al intentar duplicar el recurso.');
    console.error('Error en duplicateRow:', error);
  } finally {
    loading.closeLoading();
  }
}

onBeforeMount(async () => {
  firstRecursoTipo();
  //resetFilter();
  resetResource();
  await firstRecurso();
});
</script>

<style scoped>
.cost-panel {
  display: flex;
  align-items: flex-end;
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
  margin-bottom: 20px;
}

.cost-panel .el-button {
  margin-left: 8px;
}
</style>
